/**
 * StoryEventBubble — balão humano para eventos de story/social dos canais
 * Meta (Instagram e Messenger), no lugar do placeholder cru
 * ("[mencao em story]", "[unsupported_type]", "[reel]") e do link seco.
 *
 * COMPARTILHADO pelos dois inboxes (core vs canal): um arquivo, dois
 * ChatWindows. A mídia do story vive no CDN da Meta e expira (~24h) — o
 * onError troca a miniatura por um aviso de expirado sem quebrar o layout.
 *
 * O contexto que o AGENTE recebe para esses eventos é resolvido no backend
 * (services/meta_story_events.py); aqui é só o VISUAL do operador.
 */
import React, { useState } from 'react';
import { AtSign, Reply, Share2, Film, EyeOff, ImageOff } from 'lucide-react';

const STORY_TYPES = new Set([
  'story_mention', 'story_reply', 'ig_post', 'reel', 'share',
  'unsupported', 'unsupported_type',
]);

/** A mensagem é um evento de story/social (e não conversa direta)? */
export const isStoryEventMsg = (msg) => {
  if (!msg) return false;
  const t = String(msg.type || '').toLowerCase();
  return STORY_TYPES.has(t) || !!msg.story_url;
};

/** true quando o texto é só o placeholder interno ("[mencao em story]") —
 *  o rótulo do balão o substitui; texto REAL do cliente continua aparecendo
 *  (ex.: story_reply com mensagem digitada). */
export const isStoryPlaceholderText = (text) => {
  const t = String(text || '').trim();
  return !!t && t.startsWith('[') && t.endsWith(']');
};

const LABELS = {
  story_mention: ['Mencionou você em um story', AtSign],
  story_reply: ['Respondeu ao seu story', Reply],
  ig_post: ['Compartilhou uma publicação', Share2],
  reel: ['Compartilhou um reel', Film],
  share: ['Compartilhou um conteúdo', Share2],
  unsupported: ['Conteúdo que o canal não exibe (ex.: reel, figurinha)', EyeOff],
  unsupported_type: ['Conteúdo que o canal não exibe (ex.: reel, figurinha)', EyeOff],
};

const StoryEventBubble = ({ msg, colors }) => {
  const [broken, setBroken] = useState(false);
  const t = String(msg?.type || '').toLowerCase();
  const [label, Icon] = LABELS[t]
    || (msg?.story_url ? LABELS.story_reply : ['Interação social', Share2]);
  // story_url = o story em si; media_url = mídia anexa do evento.
  const url = msg?.story_url || msg?.media_url || null;
  const isVideo = msg?.media_type === 'video' || /\.mp4(\?|$)/i.test(url || '');

  return (
    <div style={{
      borderLeft: `3px solid ${colors.accentPrimary}`,
      paddingLeft: 8, marginBottom: 4,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 12, fontWeight: 600, opacity: 0.9,
      }}>
        <Icon size={13} style={{ flexShrink: 0 }} />
        <span>{label}</span>
      </div>

      {url && !broken && (
        isVideo ? (
          <video
            src={url}
            controls
            muted
            playsInline
            preload="metadata"
            style={{ maxWidth: 200, maxHeight: 260, borderRadius: 8 }}
            onError={() => setBroken(true)}
          />
        ) : (
          <a href={url} target="_blank" rel="noreferrer">
            <img
              src={url}
              alt=""
              style={{ maxWidth: 200, maxHeight: 260, borderRadius: 8, display: 'block' }}
              onError={() => setBroken(true)}
            />
          </a>
        )
      )}

      {url && broken && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 11, opacity: 0.65, fontStyle: 'italic',
        }}>
          <ImageOff size={12} style={{ flexShrink: 0 }} />
          A mídia deste story não está mais disponível (a Meta a remove após 24 horas).
        </div>
      )}
    </div>
  );
};

export default StoryEventBubble;
